import { Dispatch, SetStateAction, useState } from 'react';

import { EyeIcon } from '../../../../assets/svg';
import { ETPayButton } from '../../../../components/button';
import { DateAndTime } from '../../../../components/date-and-time';
import { Modal } from '../../../../components/modal';
import { Spinner } from '../../../../components/spinner';
import { Status } from '../../../../components/status';
import { ETPayTable } from '../../../../components/table';
import { useFloatingAlert } from '../../../../hooks/useFloatingAlert';
import { useMerchant } from '../../../../hooks/useMerchant';
import { Outbound } from '../../../../models/outbound';
import {
	ColumnInterface,
	Filters,
	FilterStatus,
	Pagination,
	RowInterface,
} from '../../../../models/table';
import { OutboundService } from '../../../../services/outbound';
import { validatePermission } from '../../../../utils/common';
import { errorFormatter } from '../../../../utils/errors';
import { fillStatesWithZeros } from '../../../../utils/states';
import {
	AmountContainer,
	Content,
	DetailsContainer,
	ExportModalContent,
	ExportModalDescription,
	ExportModalTitle,
	ItemText,
	Section,
	StatusContainer,
	SubText,
	Text,
	Title,
	Value,
	VoucherButton,
	VoucherContainer,
} from './styles';

interface ModalProps {
	title: string;
	message: string;
	loader: boolean;
	show: boolean;
	url: string;
}

const defaultModal: ModalProps = {
	title: 'Exportar',
	message: 'Cargando la información',
	loader: false,
	show: false,
	url: '',
};

interface OutboundTableProps {
	data: Outbound[];
	isLoading?: boolean;
	filters: Filters;
	onFilterChange: Dispatch<SetStateAction<Filters>>;
	pagination: Pagination;
	setPagination: Dispatch<SetStateAction<Pagination>>;
	totalResults?: number;
	today?: Date;
	pastDate?: Date;
	maxDate?: Date;
	stateList: FilterStatus[];
	voucherPdf?: boolean;
}

const OutboundTable = ({
	data,
	isLoading,
	filters,
	onFilterChange,
	pagination,
	setPagination,
	totalResults,
	today,
	pastDate,
	stateList,
	maxDate,
	voucherPdf,
}: OutboundTableProps) => {
	const { merchantSelected } = useMerchant();
	const flowBanksOrStp = true;
	const [modal, setModal] = useState<ModalProps>(defaultModal);
	const { handleShowAlert } = useFloatingAlert();

	const getColumns = () => {
		const columns: ColumnInterface<Outbound>[] = [
			{
				sortField: true,
				title: 'Concepto',
				propertyName: 'paymentConcept',
				cellRender: ({ paymentConcept }) => <>{paymentConcept}</>,
			},
			{
				sortField: true,
				title: 'Monto',
				align: 'right',
				propertyName: 'payoutAmount',
				cellRender: ({ payoutAmount, payoutAmountUnformatted }) => (
					<AmountContainer>
						{flowBanksOrStp ? payoutAmount : payoutAmountUnformatted}
					</AmountContainer>
				),
			},
			{
				sortField: true,
				title: 'Cuenta beneficiario',
				propertyName: 'beneficiaryAccount',
				cellRender: ({ targetBank, beneficiaryAccount }) => (
					<>
						<ItemText>
							<Text>{beneficiaryAccount}</Text>
							<SubText disabled>{targetBank}</SubText>
						</ItemText>
					</>
				),
			},
			{
				title: 'Estatus',
				align: 'center',
				propertyName: 'status',
				cellRender: ({ finalstatus }) => (
					<StatusContainer>
						<Status status={finalstatus} borderFull />
					</StatusContainer>
				),
			},
			{
				sortField: true,
				title: 'Fecha de creación',
				propertyName: 'createdatDate',
				cellRender: ({ createdatDate, createdatTime }) => (
					<DateAndTime date={createdatDate} time={createdatTime} />
				),
			},
			{
				title: 'Fecha de operación',
				propertyName: 'createdatDate',
				cellRender: ({ payoutDate, payoutTime }) => (
					<DateAndTime date={payoutDate} time={payoutTime} />
				),
			},
		];

		if (!flowBanksOrStp) {
			const filteredArray = columns.filter((_, index) => ![3, 4, 5, 6, 7].includes(index));
			filteredArray.splice(
				2,
				0,
				{
					sortField: true,
					title: 'Número de cuenta',
					propertyName: 'beneficiaryAccount',
					cellRender: ({ beneficiaryAccount }) => <>{beneficiaryAccount}</>,
				},
				{
					sortField: true,
					title: 'Nombre del beneficiario',
					propertyName: 'beneficiaryName',
					cellRender: ({ beneficiaryName }) => <>{beneficiaryName}</>,
				},
			);
			filteredArray.splice(
				5,
				0,
				{
					title: 'Divisa',
					propertyName: 'currency',
					cellRender: ({ currency }) => <>{currency}</>,
				},
				{
					title: 'Fecha',
					propertyName: 'createdatDate',
					cellRender: ({ payoutDate, payoutTime }) => (
						<DateAndTime date={payoutDate} time={payoutTime} />
					),
				},
			);

			return filteredArray;
		}

		if (voucherPdf) {
			columns.push({
				title: 'Comprobante',
				align: 'center',
				propertyName: 'voucher',
				cellRender: ({ trackingKey, createdatDate, finalstatus }) => (
					<VoucherContainer>
						{finalstatus == 'success' && (
							<VoucherButton onClick={() => handleExportPdf(trackingKey, createdatDate)}>
								<EyeIcon />
							</VoucherButton>
						)}
					</VoucherContainer>
				),
			});
		}
		if (merchantSelected?.provider == 'stp' || merchantSelected?.provider == 'tesored' || merchantSelected?.provider == null) {
			columns.push({
				title: 'CEP',
				align: 'center',
				hide: merchantSelected?.country !== 'MX',
				propertyName: 'urlCEP',
				cellRender: ({ urlCEP }) => (
					<VoucherContainer>
						{urlCEP && (
							<VoucherButton onClick={() => window.open(urlCEP)}>
								<EyeIcon />
							</VoucherButton>
						)}
					</VoucherContainer>
				),
			});
		}

		return columns;
	};

	const columns = getColumns();

	const detailSection: RowInterface<Outbound> = (data) => {
		return (
			<DetailsContainer>
				<Section>
					<Content>
						<Title>Clave de rastreo</Title>
						<Value>{data.trackingKey}</Value>
					</Content>
					{data.reference && (
						<Content>
							<Title>Referencia</Title>
							<Value>{data.reference}</Value>
						</Content>
					)}
					<Content>
						<Title>Nombre del beneficiario</Title>
						<Value>{data.beneficiaryName}</Value>
					</Content>
					<Content>
						<Title>Correo electrónico</Title>
						<Value>{data.email}</Value>
					</Content>
					{(data.detail && data.finalstatus == 'error')&& (
						<Content>
							<Title>Detalle del error</Title>
							<Value>{data.detail}</Value>
						</Content>
					)}
				</Section>
			</DetailsContainer>
		);
	};

	const handleExportAction = async () => {
		setModal({ ...modal, loader: true, show: true });

		try {
			const response = await OutboundService.getExportData({
				merchantCode: merchantSelected?.code as string,
				dateRange: {
					from: filters?.dateRange?.from || pastDate,
					to: filters?.dateRange?.to || today,
				},
				search: filters?.search,
				stateSelected: filters?.stateSelected,
			});

			if (response.url) setModal({ ...modal, loader: false, show: true, url: response.url });
		} catch (e: any) {
			handleShowAlert({
				type: 'error',
				message: errorFormatter(e.code),
			});
		}
	};

	const handleExportPdf = async (token: string, date: string) => {
		setModal({
			...modal,
			loader: true,
			show: true,
			title: 'Descargar',
			message: 'Generando comprobante',
		});

		try {
			const response = await OutboundService.getExportPdf({
				merchantCode: merchantSelected?.code as string,
				token: token,
				date: date,
				origin: 'payouts',
			});

			if (response.metadata.pdf)
				setModal({
					...modal,
					loader: false,
					show: false,
					title: 'Exportar',
					message: 'Cargando la información',
				});
			window.open(response.metadata.pdf, '_blank');
		} catch (e: any) {
			handleShowAlert({
				type: 'error',
				message: errorFormatter(e.code),
			});

			setModal({
				...modal,
				loader: false,
				show: false,
				title: 'Exportar',
				message: 'Cargando la información',
			});
		}
	};

	const handleCloseModal = () => {
		setModal(defaultModal);
	};

	const handleDownloadData = () => {
		if (modal.url) window.location.assign(modal.url);
		setModal(defaultModal);
	};

	const outboundStatesStp = ['liquidated', 'pending', 'error'];
	const outboundStatesBanks = ['liquidated'];

	return (
		<>
			{modal.show && (
				<Modal
					title={modal.title}
					show={modal.show}
					onClose={handleCloseModal}
					allowCloseModal={!modal.loader}
				>
					{modal.loader ? (
						<Spinner message={modal.message} />
					) : (
						<ExportModalContent>
							<ExportModalTitle>Tu reporte está listo</ExportModalTitle>

							<ExportModalDescription>
								Hemos terminado de recopilar los datos de tu reporte. Haz clic en el botón para
								descargarlo.
							</ExportModalDescription>
							<ETPayButton
								title="Descargar reporte"
								variant="effect"
								onClick={handleDownloadData}
							/>
						</ExportModalContent>
					)}
				</Modal>
			)}

			<ETPayTable<Outbound>
				title="Todas las salidas"
				data={data}
				isLoading={!!isLoading}
				columns={columns}
				row={flowBanksOrStp ? detailSection : undefined}
				hasFilters
				hasDateFilter
				allowExport={validatePermission('QT_EXPORT')}
				filters={filters}
				onFilterChange={onFilterChange}
				hasSearch
				exportAction={handleExportAction}
				hasPagination
				totalResults={totalResults}
				pagination={pagination}
				setPagination={setPagination}
				today={today}
				pastDate={pastDate}
				maxDate={maxDate}
				emptyMessage="No hay información disponible en el periodo seleccionado"
				stateList={fillStatesWithZeros({
					states: flowBanksOrStp ? outboundStatesStp : outboundStatesBanks,
					stateList,
				})}
				statusContent={
					flowBanksOrStp
						? [
								['liquidated', 'Liquidados'],
								['pending', 'En proceso'],
								['error', 'Fallidos'],
						  ]
						: [['liquidated', 'Liquidados']]
				}
			/>
		</>
	);
};

export { OutboundTable };
